import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Clock, User, Bell, BellSlash, Plus, Broadcast, Trash } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SchedulePage = () => {
  const { user } = useAuth();
  const [upcoming, setUpcoming] = useState([]);
  const [mySchedules, setMySchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', scheduled_at: '', duration_minutes: 60, category: 'General' });
  const isCreator = user?.role === 'creator' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const [upRes, myRes] = await Promise.all([
        axios.get(`${API}/api/schedules/upcoming`),
        isCreator ? axios.get(`${API}/api/schedules/my`) : Promise.resolve({ data: [] }),
      ]);
      setUpcoming(upRes.data);
      setMySchedules(myRes.data);
    } catch (err) {
      console.error('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  }, [isCreator]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.title || !form.scheduled_at) {
      toast.error('Title and date are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/api/schedules/create`, {
        ...form,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
      });
      toast.success('Stream scheduled!');
      setShowCreate(false);
      setForm({ title: '', description: '', scheduled_at: '', duration_minutes: 60, category: 'General' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule');
    } finally {
      setCreating(false);
    }
  };

  const handleSubscribe = async (id) => {
    try {
      await axios.post(`${API}/api/schedules/${id}/subscribe`);
      toast.success('Subscribed! You\'ll be notified when this stream goes live.');
      fetchData();
    } catch (err) {
      toast.error('Failed to subscribe');
    }
  };

  const handleCancel = async (id) => {
    try {
      await axios.delete(`${API}/api/schedules/${id}`);
      toast.success('Stream cancelled');
      fetchData();
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const timeUntil = (iso) => {
    const diff = new Date(iso) - new Date();
    if (diff < 0) return 'Starting soon';
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);
    if (days > 0) return `in ${days}d ${hours % 24}h`;
    if (hours > 0) return `in ${hours}h`;
    return `in ${Math.floor(diff / 60000)}min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24" data-testid="schedule-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Stream Schedule</h1>
          <p className="text-slate-500 text-sm mt-1">Upcoming live streams from creators</p>
        </div>
        {isCreator && (
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="px-4 py-2 bg-blue-500 text-white rounded-full font-semibold text-sm hover:bg-blue-600 transition-colors flex items-center gap-2"
            data-testid="schedule-stream-btn"
          >
            <Plus weight="bold" className="w-4 h-4" />
            Schedule Stream
          </button>
        )}
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-slate-900/80 border border-white/10 rounded-2xl p-6 mb-8" data-testid="schedule-form">
          <h3 className="text-lg font-semibold text-white mb-4">Schedule a Stream</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Title *</label>
              <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                placeholder="Stream title" className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600" data-testid="schedule-title-input" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Date & Time *</label>
              <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})}
                className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white" data-testid="schedule-datetime-input" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Duration (minutes)</label>
              <input type="number" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: parseInt(e.target.value) || 60})}
                className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white">
                <option>General</option><option>Chat</option><option>Performance</option><option>Q&A</option><option>Special Event</option>
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="text-sm text-slate-400 mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              placeholder="What's this stream about?" rows={2}
              className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-600 resize-none" />
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowCreate(false)} className="px-6 py-2.5 border border-white/10 text-slate-400 rounded-full hover:text-white transition-colors">Cancel</button>
            <button onClick={handleCreate} disabled={creating}
              className="px-6 py-2.5 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="confirm-schedule-btn"
            >
              {creating ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Calendar className="w-4 h-4" />}
              Schedule
            </button>
          </div>
        </div>
      )}

      {/* My Schedules (Creator) */}
      {isCreator && mySchedules.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">My Scheduled Streams</h2>
          <div className="space-y-3">
            {mySchedules.filter(s => s.status !== 'cancelled').map(s => (
              <div key={s.id} className="flex items-center gap-4 p-4 bg-slate-900/80 border border-white/5 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Broadcast className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{s.title}</p>
                  <p className="text-xs text-slate-500">{formatDate(s.scheduled_at)} | {s.subscriber_count} subscribers</p>
                </div>
                <button onClick={() => handleCancel(s.id)} className="p-2 text-slate-500 hover:text-red-400 transition-colors" data-testid={`cancel-schedule-${s.id}`}>
                  <Trash className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Streams */}
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Upcoming Streams</h2>
        {upcoming.length === 0 ? (
          <div className="text-center py-16 bg-slate-900/50 border border-white/5 rounded-2xl">
            <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">No upcoming streams scheduled</p>
            {isCreator && <p className="text-slate-600 text-sm mt-1">Be the first to schedule one!</p>}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {upcoming.map(s => (
              <div key={s.id} className="bg-slate-900/80 border border-white/5 rounded-2xl p-5 hover:border-blue-500/20 transition-colors" data-testid={`schedule-card-${s.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {s.creator_avatar ? (
                      <img src={s.creator_avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center"><User className="w-4 h-4 text-slate-500" /></div>
                    )}
                    <span className="text-sm font-medium text-slate-300">{s.creator_name}</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full font-medium">{timeUntil(s.scheduled_at)}</span>
                </div>
                <h3 className="text-white font-semibold mb-1">{s.title}</h3>
                {s.description && <p className="text-slate-500 text-sm mb-3 line-clamp-2">{s.description}</p>}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{s.duration_minutes}min</span>
                    <span className="flex items-center gap-1"><Bell className="w-3.5 h-3.5" />{s.subscriber_count}</span>
                    <span className="px-2 py-0.5 bg-slate-800 rounded-full">{s.category}</span>
                  </div>
                  <button
                    onClick={() => handleSubscribe(s.id)}
                    className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-full text-xs font-semibold hover:bg-blue-500/30 transition-colors flex items-center gap-1"
                    data-testid={`subscribe-schedule-${s.id}`}
                  >
                    <Bell weight="fill" className="w-3.5 h-3.5" /> Notify Me
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulePage;
